<template>
  <p-app theme="glittr">
    <template slot="header">
      <p-toolbar>
        <p-button
          secondary
          spacious
          flat
          @click="toggleSideDrawerVisible()"
        >
          <p-icon>menu</p-icon>
        </p-button>
        <router-link to="/">
          <p-img
            class="header-logo"
            width="260"
            height="40px"
            contain
            :src="require('@src/assets/logo.svg')"
          ></p-img>
        </router-link>
        <p-row>
          <p-col></p-col>
          <p-col justify-end>
            <span class="language-selection-menu-wrapper">
              <il-language-selection class="mr-3"></il-language-selection>
            </span>
            <span v-if="!$auth.isLoggedIn">
              <p-button dense @click="$auth.redirectToLogin()">
                Login
              </p-button>
            </span>
            <p-menu v-else v-model="profileMenuVisible" bottom left>
              <template slot="activator">
                <p-button color="text" dense>
                  <p-icon dense>
                    person
                  </p-icon>
                </p-button>
              </template>
              <p-card>
                <p-row column>
                  <router-link to="/_me/profile" tag="p-button">
                    {{ $t("core.page.profile.button.showProfile") }}
                  </router-link>
                  <p-button @click="doLogoff">
                    {{ $t("core.page.profile.button.logoff") }}
                    <p-icon right>
                      exit_to_app
                    </p-icon>
                  </p-button>
                </p-row>
              </p-card>
            </p-menu>
          </p-col>
        </p-row>
      </p-toolbar>
    </template>

    <p-side-drawer
      v-model="isSidedrawerVisible"
      :overlay="$media.isMobile"
      :minified.sync="isNavMinifed"
      minifiable
      color="base"
    >
      <template #drawer-header>
        <div>
          <p-row column no-gutters>
            <p-col justify-center>
              <span>{{ $format.ellipsis(username, 28) }}</span>
            </p-col>
          </p-row>
        </div>
      </template>
      <template #drawer-content>
        <p-list-group v-if="isAdmin" color="white" icon-left="widgets">
          <template #activator>
            {{ $t("navigation.widgets.name") }}
          </template>
          <p-list-entry color="white" to="/widgets/dynamic-form-config" icon-left="dynamic_form" @click="navigationClicked">
            {{ $t("navigation.widgets.dynamic-form-configuration.name") }}
          </p-list-entry>
          <p-list-entry color="white" to="/salvationarmy/il/widgets/digital-donation-receipt-config" icon-left="dynamic_form" @click="navigationClicked">
            {{ $t("navigation.widgets.digital-donation-receipt-configuration.name") }}
          </p-list-entry>
        </p-list-group>
        <p-list-group>
          <p-list spacious>
            <p-list-entry
              v-for="nav in navigation"
              :key="nav.caption"
              color="white"
              :icon-left="nav.icon"
              :to="nav.to"
              @click="navigationClicked"
            >
              {{ nav.caption }}
            </p-list-entry>
            <p-list-group v-if="isAdmin" color="white" icon-left="memory">
              <template #activator>
                Integration
              </template>
              <p-list-entry color="white" to="/_admin/processes" icon-left="account_tree" @click="navigationClicked">
                {{ $t("core.page.processes.page.title") }}
              </p-list-entry>
              <p-list-entry color="white" :to="`/_admin/integrationlog?dateFrom=${date}`" icon-left="merge_type" @click="navigationClicked">
                {{ $t("core.page.integrationlog.page.title") }}
              </p-list-entry>
              <p-list-entry color="white" to="/_admin/contactidmapping" icon-left="social_distance" @click="navigationClicked">
                {{ $t("core.page.contactidmapping.page.title") }}
              </p-list-entry>
              <p-list-entry color="white" to="/_admin/fieldmapping" icon-left="compare_arrows" @click="navigationClicked">
                {{ $t('core.page.fieldmapping.page.list.title') }}
              </p-list-entry>
              <p-list-entry color="white" to="/_admin/request-form" icon-left="rule_folder" @click="navigationClicked">
                {{ $t('core.page.requestForm.page.title') }}
              </p-list-entry>
              <p-list-entry color="white" to="/widgets/widget-host-config" icon-left="settings" @click="navigationClicked">
                {{ $t('core.page.widgetHostConfiguration.page.title') }}
              </p-list-entry>
            </p-list-group>
          </p-list>
        </p-list-group>
      </template>
      <template v-if="isAdmin" #drawer-footer-actions>
        <p-button :secondary="isSettingsVisible" :tertiary="!isSettingsVisible" @click="toggleSettings">
          <p-icon>settings</p-icon>
        </p-button>
      </template>
      <template #content>
        <p-side-drawer v-model="isSettingsVisible" :overlay="$media.isMobile">
          <template #drawer-header-image>
            <p-icon>settings</p-icon>
          </template>
          <template #drawer-header-title>
            {{ $t("admin.settings.page.title") }}
          </template>
          <template #drawer-content>
            <p-list-group>
              <p-list class="mt-3" spacious>
                <p-list-entry to="/_admin/dashboard" icon-left="dashboard" @click="settingsClicked">
                  {{ $t('core.page.dashboard.page.title') }}
                </p-list-entry>
                <p-list-entry :to="`/_admin/applog?levels=error&dateFrom=${date}`" icon-left="list" @click="settingsClicked">
                  {{ $t('core.page.appLog.page.title') }}
                </p-list-entry>
                <p-list-entry :to="'/_admin/calllog?datefrom=${date}'" icon-left="list">
                  {{ $t('core.page.callLog.page.title') }}
                </p-list-entry>
                <p-list-entry to="/_admin/users" icon-left="person" @click="settingsClicked">
                  {{ $t('core.page.user.page.title') }}
                </p-list-entry>
                <p-list-entry to="/_admin/sequencenumbers" icon-left="pin" @click="settingsClicked">
                  {{ $t('core.page.sequencenumbers.page.title') }}
                </p-list-entry>
                <p-list-entry to="/_admin/textcontent" icon-left="translate" @click="settingsClicked">
                  {{ $t('core.page.textContent.page.title') }}
                </p-list-entry>
              </p-list>
            </p-list-group>
          </template>
          <template v-if="$media.isMobile" #drawer-footer-actions>
            <p-button tertiary @click="isSettingsVisible = false">
              <p-icon>arrow_back</p-icon>
            </p-button>
          </template>
          <template #content>
            <slot></slot>
          </template>
        </p-side-drawer>
      </template>
    </p-side-drawer>
  </p-app>
</template>

<script lang="ts">
  import Vue, { PropType } from 'vue';
  import User from '@glittr/frontend-core/src/plugins/auth/user';

  export default Vue.extend({
    auth: true,
    props: {
      hasBackButton: { type: Boolean, default: false },
      navigation: { type: Array as PropType<{ caption: string, to: string, icon: string }[]>, default: () => [] },
    },
    data: () => ({
      date: Vue.$date.now().format('YYYY-MM-DD'),
      profileMenuVisible: false,
      isSidedrawerVisible: Vue.$media.isNotMobile,
      isSettingsVisible: false,
      isNavMinifed: false,
    }),
    computed: {
      user(): User {
        return this.$auth.user ?? ({} as User);
      },
      username(): string {
        return this.$auth.user?.userName!;
      },
      isHostAdmin(): boolean {
        let isAdmin = false;
        if (this.$auth.isLoggedIn) {
          isAdmin = this.$auth.isInRole('HostAdmin');
        }
        return isAdmin;
      },
      isAdmin(): boolean {
        let isAdmin = false;
        if (this.$auth.isLoggedIn) {
          isAdmin = this.$auth.isInRole('Admin', 'HostAdmin');
        }
        return isAdmin;
      },
    },
    watch: {
      isNavMinifed() {
        this.$sessionStorage.set('isNavMinifed', this.isNavMinifed);
      },
    },
    beforeMount() {
      if (this.$media.isNotMobile) {
        this.isSidedrawerVisible = this.$sessionStorage.get<boolean>('isSidedrawerVisible') ?? true;
        this.isSettingsVisible = this.isAdmin && (this.$sessionStorage.get<boolean>('isSettingsVisible') ?? false);
        this.isNavMinifed = this.$sessionStorage.get<boolean>('isNavMinifed') ?? false;
      }
    },
    methods: {
      toggleSettings() {
        this.isSettingsVisible = !this.isSettingsVisible;
        if (this.$media.isMobile) {
          this.toggleSideDrawerVisible(false);
        } else {
          this.isNavMinifed = this.isSettingsVisible;
        }
        this.$sessionStorage.set('isSettingsVisible', this.isSettingsVisible);
      },
      navigationClicked() {
        if (this.$media.isMobile) {
          this.toggleSideDrawerVisible(false);
        }
      },
      settingsClicked() {
        if (this.$media.isMobile) {
          this.toggleSettings();
        }
      },
      async doLogoff() {
        this.$router.push(this.$config.values['auth-redirects-logout']);
      },
      toggleSideDrawerVisible(isVisible?: boolean) {
        const newVisibleState = isVisible ?? !this.isSidedrawerVisible;
        this.isSidedrawerVisible = newVisibleState;
        this.$sessionStorage.set('isSidedrawerVisible', this.isSidedrawerVisible);
      },
    },
  });
</script>
